import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';

const FAQ = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  // FAQ questions and answers
  const faqs = [
    {
      question: "What is MyEventShop?",
      answer: "MyEventShop is an all-in-one event platform that helps creators manage, promote, and monetize their events. While currently, we help our creators host and ticket their events, we are looking at building other marketing tools like surveys and feedback, newsletters, social media scheduling and more."
    },
    {
      question: "How to create an event?",
      answer: "MyEventShop has a simple single-form setup. As an event organiser, follow the steps below to create your own event: Sign up or log in, click on Create Event on the main menu (top right), put in the event details (compulsory fields are marked by a star), click on the 'Create Event' button at the bottom. Go to 'My Bookings' and click on your event to avail statistics and details of tickets booked. Under 'My Account', kindly keep the details up to date for troubleshooting."
    },
    {
      question: "How to book a ticket?",
      answer: "You can browse events and book a ticket by following the steps below: Sign up or log in, click on the event, book a ticket, ticket confirmation via email, save it on your calendar. To revisit your bookings: Log in, go to 'My Bookings', click on the ticket to view it."
    },
    {
      question: "Is MyEventShop free to use?",
      answer: "MyEventShop offers both free and paid options. You can create and publish events for free, with a small fee applied only when priced tickets are sold."
    },
    {
      question: "How much does MyEventShop charge?",
      answer: "There's a 5% service fee per ticket sold. Every payment transaction has additional charges, including service charges by Stripe and value added tax at 23%. No charges for free tickets."
    },
    {
      question: "How to cancel an event?",
      answer: "The event organiser must email admin@myeventshop.com, and we will put a cancelled badge on the event."
    },
    {
      question: "Refunds policy",
      answer: "Every event has a compulsory field for the organiser to mention their cancellation and refund policy. Refunds will be made according to the organiser's policy. For free events, mention 'Not Applicable' in the field."
    },
    {
      question: "Refunds for event cancellation",
      answer: "If an event is cancelled, the event organiser must intimate MyEventShop via email. All ticket holders will be refunded the full amount by MyEventShop. The event organiser shall also provide all the necessary details about the attendee for the purpose of refund. The event organiser must email ticket holders with the cancellation notice."
    },
    {
      question: "How to cancel a ticket?",
      answer: "You can access the ticket under 'My Bookings'. Click on the cancel button."
    },
    {
      question: "Ticket refund policy",
      answer: "Refund depends on the policy of the organiser, as mentioned on the event page. After cancelling the ticket, get in touch with the organiser with your 'Cancellation confirmation' email. This should be before the event or within 48 hours after the event start time. Once the organiser confirms a payment with MyEventShop, we will process the refund within 7 business days of the intimation. Payment will be the ticket price, excluding platform fees and tax."
    },
    {
      question: "Enquiries",
      answer: "Send your enquiries to admin@myeventshop.com."
    }
  ];

  return (
    <Box>
      <AppMenu 
        onLoginClick={() => setIsLoginOpen(true)} 
        onSignupClick={() => setIsSignupOpen(true)} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 5, md: 8 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary" 
            fontWeight="bold"
            gutterBottom
          >
            Frequently Asked Questions
          </Typography>
          <Typography 
            variant="h6"
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto', 
              mb: 4,
              color: 'text.secondary'
            }}
          >
            Find answers to the most common questions about MyEventShop
          </Typography>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ mb: 8 }}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            {faqs.map((faq, faqIndex) => {
              const panelId = `panel-${faqIndex}`;
              return (
                <Accordion 
                  key={panelId}
                  expanded={expanded === panelId}
                  onChange={handleChange(panelId)}
                  sx={{ 
                    mb: 2,
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${panelId}-content`}
                    id={`${panelId}-header`}
                    sx={{ 
                      bgcolor: 'rgba(95, 60, 111, 0.03)',
                      '&:hover': { bgcolor: 'rgba(95, 60, 111, 0.06)' } 
                    }}
                  >
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="medium"
                      sx={{ fontSize: '1.05rem' }}
                    >
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 2 }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Paper>
        </Box>
        
        {/* Still Have Questions Section */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            mb: 8,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: 'white',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" component="h2" mb={2} fontWeight="bold">
            Still Have Questions?
          </Typography>
          <Typography variant="body1" paragraph>
            Can't find the answer you're looking for? Please contact our support team directly.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Email us at: <strong>admin@myeventshop.com</strong>
          </Typography>
        </Paper>
      </Container>
      
      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default FAQ; 